import { useContext } from "react";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { useOptionalApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";

/** A count against a limit; `limit` -1 means unlimited. */
export interface PlanMeter {
  used: number;
  limit: number;
  resetsAt: string | null;
}

/** `GET /api/plan`: every number the plan surfaces show comes from here (launch redesign, rule 5). */
export interface PlanUsage {
  /** False while limits are shown nowhere and applied to nothing. */
  enforced: boolean;
  plan: { slug: string; name: string };
  documents: PlanMeter;
  imports: PlanMeter;
  /** How far through the month's Ask Lilia allowance, 0–1: never a balance. */
  ask: { fraction: number; unlimited: boolean; resetsAt: string; enforced: boolean };
}

export const planQueryKey = ["plan"] as const;

/** `enabled` false keeps a surface that isn't showing from asking (the limit dialog, until it opens). */
/** For views rendered without the app's providers (Ask Lilia's panel in isolation): the plan stays unknown. */
const FALLBACK = new QueryClient();

export function usePlan(enabled = true) {
  const api = useOptionalApiContext();
  const queries = useContext(QueryClientContext);
  return useQuery(
    {
      queryKey: planQueryKey,
      queryFn: async () => handleResponse<PlanUsage>(await api!.fetchWithAuth(`${API_BASE}/plan`)),
      // Not gated on the Stytch session: the other signed-in queries aren't either, and a test
      // build's session isn't a Stytch one, so a gate here left the rail's plan line blank.
      enabled: enabled && !!api && !!queries,
      staleTime: 30_000,
    },
    queries ?? FALLBACK,
  );
}
